import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { shopApi } from '../../api/shop';
import { useAuth } from '../../auth/AuthProvider';
import { RequireShop } from '../../components/ShopSelector';
import { SectionCard } from '../../components/PageChrome';
import {
  EmptyState,
  ErrorBanner,
  LoadingState,
  Modal,
  PageHeader,
} from '../../components/Layout';
import { useShop } from '../../context/ShopContext';

const emptyCustomer = {
  email: '',
  first_name: '',
  last_name: '',
  phone: '',
  password: 'changeme123',
};

export default function CustomersPage() {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { selectedShopId } = useShop();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyCustomer);
  const [saving, setSaving] = useState(false);

  const loadCustomers = useCallback(async () => {
    if (!selectedShopId) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const data = await shopApi.listCustomers(token, selectedShopId);
      setCustomers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, selectedShopId]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const handleCreate = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      const created = await shopApi.createCustomer(token, selectedShopId, form);
      setModalOpen(false);
      setForm(emptyCustomer);
      navigate(`/shop/customers/${created.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <RequireShop>
      <PageHeader
        title="Customers"
        description="Manage buyer accounts, profiles, and saved addresses."
        actions={
          <button type="button" className="btn btn-primary" onClick={() => setModalOpen(true)}>
            Add customer
          </button>
        }
      />

      <ErrorBanner message={error} />

      {loading ? (
        <LoadingState />
      ) : customers.length === 0 ? (
        <EmptyState message="No customers yet." />
      ) : (
        <SectionCard title="Customer directory">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {customers.map((customer) => (
                <tr key={customer.id}>
                  <td>
                    <Link to={`/shop/customers/${customer.id}`}>
                      {customer.first_name} {customer.last_name}
                    </Link>
                  </td>
                  <td>{customer.email}</td>
                  <td>{customer.phone || '—'}</td>
                  <td>
                    <span className={`badge ${customer.is_active ? 'badge-success' : 'badge-muted'}`}>
                      {customer.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="row-actions">
                      <Link
                        to={`/shop/customers/${customer.id}`}
                        className="btn btn-secondary btn-sm"
                      >
                        View
                      </Link>
                      <Link
                        to={`/shop/orders/new?customer_id=${customer.id}`}
                        className="btn btn-secondary btn-sm"
                      >
                        Order
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionCard>
      )}

      <Modal
        title="Add customer"
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </button>
            <button type="submit" form="create-customer-form" className="btn btn-primary" disabled={saving}>
              {saving ? 'Creating…' : 'Create customer'}
            </button>
          </>
        }
      >
        <form id="create-customer-form" className="form-grid" onSubmit={handleCreate}>
          <div className="form-field">
            <label htmlFor="customer-email">Email</label>
            <input
              id="customer-email"
              type="email"
              value={form.email}
              onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
              required
            />
          </div>
          <div className="form-grid two-col">
            <div className="form-field">
              <label htmlFor="customer-first">First name</label>
              <input
                id="customer-first"
                value={form.first_name}
                onChange={(event) => setForm((prev) => ({ ...prev, first_name: event.target.value }))}
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="customer-last">Last name</label>
              <input
                id="customer-last"
                value={form.last_name}
                onChange={(event) => setForm((prev) => ({ ...prev, last_name: event.target.value }))}
                required
              />
            </div>
          </div>
          <div className="form-field">
            <label htmlFor="customer-phone">Phone</label>
            <input
              id="customer-phone"
              value={form.phone}
              onChange={(event) => setForm((prev) => ({ ...prev, phone: event.target.value }))}
            />
          </div>
          <div className="form-field">
            <label htmlFor="customer-password">Temporary password</label>
            <input
              id="customer-password"
              type="password"
              value={form.password}
              onChange={(event) => setForm((prev) => ({ ...prev, password: event.target.value }))}
              minLength={8}
              required
            />
          </div>
        </form>
      </Modal>
    </RequireShop>
  );
}
